import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Truck, Target, Zap, Key, Fuel, Anchor, Battery, Wrench,
  MapPin, DollarSign, Clock, Star, Phone, MessageSquare, 
  Check, X, Navigation, Loader2, AlertTriangle, ChevronRight,
  Briefcase, TrendingUp, Settings, RefreshCw, Play, LocateFixed,
  Crown, Award, Shield, Car, ExternalLink, MapPinned, CreditCard, Calendar
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Local components
import BillingHistory from './components/BillingHistory';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Service icons mapping
const SERVICE_ICONS = {
  towing: Truck,
  tire_change: Target,
  jump_start: Zap,
  lockout: Key,
  fuel_delivery: Fuel,
  winch_out: Anchor,
  battery_replace: Battery,
  mobile_mechanic: Wrench,
};

// Status colors
const STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  accepted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  en_route: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  arrived: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  in_progress: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Provider Rating Badge Component
const ProviderRatingBadge = ({ rating, ratingCount, isPremium }) => {
  // Determine badge based on rating and review count
  let badge = null;
  let badgeText = null;
  
  if (rating && rating >= 4.5 && ratingCount >= 3) {
    badge = 'top_rated';
    badgeText = 'Top Rated';
  } else if (rating && rating >= 4.0 && ratingCount >= 2) {
    badge = 'highly_rated';
    badgeText = 'Highly Rated';
  } else if (isPremium) {
    badge = 'premium';
    badgeText = 'Premium';
  }
  
  if (!badge) return null;
  
  const badgeStyles = {
    top_rated: {
      bg: 'bg-amber-500/20',
      border: 'border-amber-500/50',
      text: 'text-amber-400',
      icon: Crown
    },
    highly_rated: {
      bg: 'bg-blue-500/20',
      border: 'border-blue-500/50',
      text: 'text-blue-400',
      icon: Award
    },
    premium: {
      bg: 'bg-purple-500/20',
      border: 'border-purple-500/50',
      text: 'text-purple-400',
      icon: Shield
    }
  };
  
  const style = badgeStyles[badge];
  const Icon = style.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.border} ${style.text} border`}>
      <Icon className="h-3 w-3" />
      {badgeText}
    </span>
  );
};

export default function RoadsideProviderPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  
  // Provider profile
  const [profile, setProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  
  // Jobs
  const [availableJobs, setAvailableJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  // Earnings
  const [earnings, setEarnings] = useState(null);
  
  // Selected job for actions
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [processingJob, setProcessingJob] = useState(false);
  
  // Setup mode
  const [needsSetup, setNeedsSetup] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  
  // Setup form
  const [setupForm, setSetupForm] = useState({
    business_name: '',
    phone: '',
    services: [],
    pricing: {},
    coverage_zips: '',
    description: '',
    license_number: '',
    insurance_info: ''
  });
  
  // Service types
  const [serviceTypes, setServiceTypes] = useState([]);
  
  // Premium subscription
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [processingPremium, setProcessingPremium] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchServiceTypes();
    fetchProfile();
    fetchPremiumStatus();
  }, []);

  // Polling for jobs when online
  useEffect(() => {
    if (!isOnline) return;
    
    const interval = setInterval(() => {
      fetchAvailableJobs();
      fetchActiveJobs();
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(interval);
  }, [isOnline]);

  // Location tracking when provider has active jobs
  useEffect(() => {
    if (activeJobs.length === 0) return;
    
    // Track location every 10 seconds when provider has active jobs
    const updateLocation = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await api.put('/roadside/provider/location', {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude
            });
          } catch (error) {
            console.error('Failed to update location:', error);
          }
        },
        (err) => console.error('Geolocation error:', err),
        { enableHighAccuracy: true }
      );
    };
    
    // Update immediately and then every 10 seconds
    updateLocation();
    const interval = setInterval(updateLocation, 10000);
    
    return () => clearInterval(interval);
  }, [activeJobs.length, api]);

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get('/roadside/service-types');
      setServiceTypes(res.data.services || []);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/roadside/provider/profile');
      if (res.data) {
        setProfile(res.data);
        setIsOnline(res.data.is_online || false);
        setNeedsSetup(false);
        fetchAvailableJobs();
        fetchActiveJobs();
        fetchEarnings();
      } else {
        setNeedsSetup(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNeedsSetup(true);
      } else {
        console.error('Failed to fetch profile:', error);
      }
    }
    setLoading(false);
  };

  const fetchAvailableJobs = async () => {
    try {
      const res = await api.get('/roadside/provider/available-jobs');
      setAvailableJobs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch available jobs:', error);
    }
  };

  const fetchActiveJobs = async () => {
    try {
      const res = await api.get('/roadside/provider/active-jobs');
      setActiveJobs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch active jobs:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const res = await api.get('/roadside/provider/earnings');
      setEarnings(res.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      const res = await api.get('/roadside/provider/premium/status');
      setPremiumStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch premium status:', error);
    }
  };

  const handleSubscribePremium = async () => {
    setProcessingPremium(true);
    try {
      await api.post('/roadside/provider/premium/subscribe', { payment_method: 'card' });
      toast.success('Successfully subscribed to Premium!');
      setShowPremiumDialog(false);
      fetchPremiumStatus();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    }
    setProcessingPremium(false);
  };

  const handleCancelPremium = async () => {
    if (!window.confirm('Are you sure you want to cancel your premium subscription?')) return;
    
    setProcessingPremium(true);
    try {
      await api.post('/roadside/provider/premium/cancel');
      toast.success('Premium subscription cancelled');
      fetchPremiumStatus();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
    setProcessingPremium(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchAvailableJobs(), fetchActiveJobs()]);
    setRefreshing(false);
    toast.success('Jobs refreshed');
  };

  const toggleOnlineStatus = async () => {
    setTogglingStatus(true);
    try {
      await api.put('/roadside/provider/status', null, {
        params: { is_online: !isOnline }
      });
      setIsOnline(!isOnline);
      toast.success(`You are now ${!isOnline ? 'online' : 'offline'}`);
      if (!isOnline) {
        fetchAvailableJobs();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
    setTogglingStatus(false);
  };

  const handleAcceptJob = async (job) => {
    setProcessingJob(true);
    try {
      const res = await api.post(`/roadside/provider/accept-job/${job.id}`);
      toast.success(res.data.message);
      // In production, redirect to payment flow
      setShowJobDialog(false);
      fetchAvailableJobs();
      fetchActiveJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    }
    setProcessingJob(false);
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    setProcessingJob(true);
    try {
      await api.put(`/roadside/provider/job/${jobId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setShowJobDialog(false);
      fetchActiveJobs();
      if (newStatus === 'completed') {
        fetchEarnings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
    setProcessingJob(false);
  };

  const handleSetupSubmit = async () => {
    setProcessingJob(true);
    try {
      const data = {
        ...setupForm,
        coverage_zips: setupForm.coverage_zips.split(',').map(z => z.trim()).filter(Boolean)
      };
      await api.post('/roadside/provider/setup', data);
      toast.success('Provider profile created!');
      setShowSetup(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
    setProcessingJob(false);
  };

  const toggleService = (serviceId) => {
    const services = setupForm.services.includes(serviceId)
      ? setupForm.services.filter(s => s !== serviceId)
      : [...setupForm.services, serviceId];
    setSetupForm({ ...setupForm, services });
  };

  // Setup Screen
  if (needsSetup && !showSetup) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 md:p-6 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Become a Provider</CardTitle>
            <CardDescription>
              Set up your profile to start receiving roadside assistance jobs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="p-6 rounded-full bg-blue-500/10 border border-blue-500/30">
                <Truck className="h-12 w-12 text-blue-400" />
              </div>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>Set your services and pricing</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>Define your coverage area by ZIP codes</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>Receive job notifications in real-time</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>Earn money helping stranded drivers</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button className="w-full" onClick={() => setShowSetup(true)}>
              Get Started
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Setup Form
  if (showSetup) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Provider Setup</CardTitle>
              <CardDescription>Complete your profile to start receiving jobs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={setupForm.business_name}
                    onChange={(e) => setSetupForm({ ...setupForm, business_name: e.target.value })}
                    placeholder="Your Towing Company"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    value={setupForm.phone}
                    onChange={(e) => setSetupForm({ ...setupForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>License Number</Label>
                  <Input
                    value={setupForm.license_number}
                    onChange={(e) => setSetupForm({ ...setupForm, license_number: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              {/* Services */}
              <div>
                <Label className="mb-3 block">Services Offered *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {serviceTypes.map((service) => {
                    const IconComponent = SERVICE_ICONS[service.id] || Wrench;
                    const isSelected = setupForm.services.includes(service.id);
                    return (
                      <Card
                        key={service.id}
                        className={`cursor-pointer transition-all p-2 ${
                          isSelected ? 'border-blue-500 bg-blue-500/10' : ''
                        }`}
                        onClick={() => toggleService(service.id)}
                      >
                        <div className="flex flex-col items-center text-center gap-1">
                          <IconComponent className={`h-5 w-5 ${isSelected ? 'text-blue-400' : ''}`} />
                          <span className="text-xs">{service.name}</span>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
              
              {/* Pricing */}
              {setupForm.services.length > 0 && (
                <div>
                  <Label className="mb-3 block">Your Pricing</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {setupForm.services.map((serviceId) => {
                      const service = serviceTypes.find(s => s.id === serviceId);
                      return (
                        <div key={serviceId} className="flex items-center gap-2">
                          <span className="text-sm min-w-[100px]">{service?.name}</span>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <Input
                              type="number"
                              className="pl-7"
                              placeholder="0"
                              value={setupForm.pricing[serviceId] || ''}
                              onChange={(e) => setSetupForm({
                                ...setupForm,
                                pricing: { ...setupForm.pricing, [serviceId]: parseFloat(e.target.value) || 0 }
                              })}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Coverage Area */}
              <div>
                <Label>Coverage ZIP Codes *</Label>
                <Textarea
                  value={setupForm.coverage_zips}
                  onChange={(e) => setSetupForm({ ...setupForm, coverage_zips: e.target.value })}
                  placeholder="90001, 90002, 90003..."
                  className="h-20"
                />
                <p className="text-xs text-slate-400 mt-1">Enter ZIP codes separated by commas</p>
              </div>
              
              {/* Description */}
              <div>
                <Label>About Your Business</Label>
                <Textarea
                  value={setupForm.description}
                  onChange={(e) => setSetupForm({ ...setupForm, description: e.target.value })}
                  placeholder="Tell customers about your services..."
                  className="h-20"
                />
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSetup(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSetupSubmit}
                disabled={!setupForm.business_name || !setupForm.phone || setupForm.services.length === 0 || !setupForm.coverage_zips || processingJob}
              >
                {processingJob ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Complete Setup
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  // Job Card Component
  const JobCard = ({ job, isAvailable = false }) => {
    const IconComponent = SERVICE_ICONS[job.service_type] || Wrench;
    const statusColor = STATUS_COLORS[job.status] || STATUS_COLORS.pending;
    
    // Calculate time ago
    const timeAgo = (dateStr) => {
      const now = new Date();
      const then = new Date(dateStr);
      const diffMins = Math.floor((now - then) / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      return `${Math.floor(diffHrs / 24)}d ago`;
    };
    
    return (
      <Card 
        className="cursor-pointer hover:border-blue-500/50 transition-all group"
        onClick={() => {
          setSelectedJob(job);
          setShowJobDialog(true);
        }}
        data-testid={`job-${job.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isAvailable ? 'bg-amber-500/10' : 'bg-blue-500/10'}`}>
                <IconComponent className={`h-5 w-5 ${isAvailable ? 'text-amber-400' : 'text-blue-400'}`} />
              </div>
              <div>
                <p className="font-medium">{job.service_name}</p>
                <div className="flex items-center gap-1 text-sm text-slate-400">
                  <MapPin className="h-3 w-3" />
                  <span>{job.location_approximate?.city || 'Unknown'}, {job.location_approximate?.state || ''}</span>
                  {job.location_approximate?.zip && (
                    <span className="text-slate-500">• {job.location_approximate.zip}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge className={statusColor}>
                {job.status?.replace('_', ' ')}
              </Badge>
              {isAvailable && profile?.pricing?.[job.service_type] && (
                <span className="text-green-400 font-bold text-lg">
                  ${profile.pricing[job.service_type]}
                </span>
              )}
            </div>
          </div>
          
          {/* Vehicle info */}
          {job.vehicle && (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-400 bg-slate-800/30 rounded-md px-2 py-1.5">
              <Car className="h-4 w-4" />
              <span>{job.vehicle.year} {job.vehicle.make} {job.vehicle.model}</span>
              {job.vehicle.color && <span className="text-slate-500">• {job.vehicle.color}</span>}
            </div>
          )}
          
          {/* Footer with time and actions */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              <Clock className="h-3 w-3 inline mr-1" />
              {timeAgo(job.created_at)}
            </span>
            
            {/* Quick actions for active jobs */}
            {!isAvailable && job.status !== 'completed' && job.status !== 'cancelled' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-400 hover:text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/roadside/tracking/${job.id}`);
                }}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Track
              </Button>
            )}
          </div>
          
          {/* Notes if present */}
          {job.notes && (
            <div className="mt-2 text-xs text-slate-500 border-t border-slate-800 pt-2">
              <MessageSquare className="h-3 w-3 inline mr-1" />
              {job.notes.length > 50 ? job.notes.substring(0, 50) + '...' : job.notes}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="roadside-provider-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{profile?.business_name || 'Provider Dashboard'}</h1>
              <ProviderRatingBadge 
                rating={earnings?.rating} 
                ratingCount={earnings?.rating_count} 
                isPremium={profile?.is_premium}
              />
            </div>
            <p className="text-slate-400 text-sm">Roadside Rescue Provider</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isOnline ? 'text-green-400' : 'text-slate-400'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
              <Switch
                checked={isOnline}
                onCheckedChange={toggleOnlineStatus}
                disabled={togglingStatus}
                data-testid="online-toggle"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowSetup(true)}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        {earnings && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="bg-slate-900/50">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-5 w-5 mx-auto text-green-400 mb-1" />
                <p className="text-2xl font-bold">${earnings.total_earnings?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-slate-400">Total Earnings</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50">
              <CardContent className="p-4 text-center">
                <Briefcase className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                <p className="text-2xl font-bold">{earnings.total_jobs || 0}</p>
                <p className="text-xs text-slate-400">Total Jobs</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50">
              <CardContent className="p-4 text-center">
                <Star className="h-5 w-5 mx-auto text-amber-400 mb-1" />
                <p className="text-2xl font-bold">{earnings.rating?.toFixed(1) || '-'}</p>
                <p className="text-xs text-slate-400">Rating ({earnings.rating_count || 0})</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50">
              <CardContent className="p-4 text-center">
                <TrendingUp className="h-5 w-5 mx-auto text-purple-400 mb-1" />
                <p className="text-2xl font-bold">{earnings.jobs_this_week || 0}</p>
                <p className="text-xs text-slate-400">This Week</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Premium Upsell Card */}
        {premiumStatus && !premiumStatus.is_premium && (
          <Card className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 border-amber-700/30 mb-6 cursor-pointer hover:border-amber-600/50 transition-colors" onClick={() => setShowPremiumDialog(true)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500 rounded-lg">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-bold">Upgrade to Premium</p>
                      <Badge className="bg-green-600/20 text-green-400 border-green-600/30">50% Off Fees</Badge>
                    </div>
                    <p className="text-slate-400 text-sm">Get priority listing, reduced fees, and more jobs</p>
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-amber-400 font-bold text-xl">${premiumStatus.monthly_fee}/mo</p>
                  <p className="text-xs text-slate-400">Cancel anytime</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Premium Active Card */}
        {premiumStatus?.is_premium && (
          <Card className="bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border-amber-500/50 mb-6" data-testid="premium-status-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500 rounded-lg">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-bold">Premium Provider</p>
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Active</Badge>
                    </div>
                    <p className="text-slate-400 text-sm">Enjoying reduced fees, priority listing & featured badge</p>
                    {premiumStatus.subscribed_at && (
                      <p className="text-xs text-slate-500 mt-1">
                        Member since {new Date(premiumStatus.subscribed_at).toLocaleDateString()}
                        {premiumStatus.next_billing_date && (
                          <> • Next billing: {new Date(premiumStatus.next_billing_date).toLocaleDateString()}</>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowSubscriptionDialog(true)}
                    data-testid="manage-subscription-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    Manage
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Jobs Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="available" className="gap-2">
                Available Jobs
                {availableJobs.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{availableJobs.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="active" className="gap-2">
                Active Jobs
                {activeJobs.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{activeJobs.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="billing" className="gap-2">
                Billing
              </TabsTrigger>
            </TabsList>
            
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <TabsContent value="available" className="space-y-3">
            {!isOnline ? (
              <Card className="bg-amber-500/5 border-amber-500/30">
                <CardContent className="p-6 text-center">
                  <AlertTriangle className="h-8 w-8 mx-auto text-amber-400 mb-2" />
                  <p className="text-amber-400 font-medium">You're currently offline</p>
                  <p className="text-sm text-slate-400 mt-1">Go online to see available jobs in your area</p>
                  <Button className="mt-4" onClick={toggleOnlineStatus}>
                    Go Online
                  </Button>
                </CardContent>
              </Card>
            ) : availableJobs.length === 0 ? (
              <Card className="bg-slate-800/50">
                <CardContent className="p-6 text-center">
                  <Clock className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-400">No available jobs right now</p>
                  <p className="text-sm text-slate-500 mt-1">New jobs will appear here in real-time</p>
                </CardContent>
              </Card>
            ) : (
              availableJobs.map((job) => (
                <JobCard key={job.id} job={job} isAvailable />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="active" className="space-y-3">
            {activeJobs.length === 0 ? (
              <Card className="bg-slate-800/50">
                <CardContent className="p-6 text-center">
                  <Briefcase className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-400">No active jobs</p>
                  <p className="text-sm text-slate-500 mt-1">Accept a job to see it here</p>
                </CardContent>
              </Card>
            ) : (
              activeJobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="billing" className="space-y-3">
            <BillingHistory />
          </TabsContent>
        </Tabs>
        
        {/* Job Detail Dialog */}
        <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
          <DialogContent className="max-w-lg">
            {selectedJob && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {React.createElement(SERVICE_ICONS[selectedJob.service_type] || Wrench, {
                      className: 'h-5 w-5 text-blue-400'
                    })}
                    {selectedJob.service_name}
                  </DialogTitle>
                  <Badge className={STATUS_COLORS[selectedJob.status]}>
                    {selectedJob.status?.replace('_', ' ')}
                  </Badge>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Location */}
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Location</p>
                      {selectedJob.location ? (
                        <p className="text-sm text-slate-400">
                          {selectedJob.location.address}, {selectedJob.location.city}, {selectedJob.location.state} {selectedJob.location.zip}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400">
                          {selectedJob.location_approximate?.city}, {selectedJob.location_approximate?.state}
                          <span className="text-amber-400 ml-2">(Exact address after accepting)</span>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Vehicle */}
                  {selectedJob.vehicle && (
                    <div className="flex items-start gap-3">
                      <Truck className="h-5 w-5 text-amber-400 mt-0.5" />
                      <div>
                        <p className="font-medium">Vehicle</p>
                        <p className="text-sm text-slate-400">
                          {selectedJob.vehicle.year} {selectedJob.vehicle.make} {selectedJob.vehicle.model}
                          {selectedJob.vehicle.color && ` • ${selectedJob.vehicle.color}`}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Description */}
                  {selectedJob.description && (
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-purple-400 mt-0.5" />
                      <div>
                        <p className="font-medium">Details</p>
                        <p className="text-sm text-slate-400">{selectedJob.description}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Customer Contact (only if accepted) */}
                  {selectedJob.status !== 'pending' && selectedJob.customer_name && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-cyan-400 mt-0.5" />
                      <div>
                        <p className="font-medium">Customer</p>
                        <p className="text-sm text-slate-400">{selectedJob.customer_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Price */}
                  {profile?.pricing?.[selectedJob.service_type] && (
                    <Card className="bg-green-500/5 border-green-500/30">
                      <CardContent className="p-3 flex justify-between items-center">
                        <span>Your Rate</span>
                        <span className="text-xl font-bold text-green-400">
                          ${profile.pricing[selectedJob.service_type]}
                        </span>
                      </CardContent>
                    </Card>
                  )}
                </div>
                
                <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                  {/* Available job actions */}
                  {selectedJob.status === 'pending' && (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-500"
                      onClick={() => handleAcceptJob(selectedJob)}
                      disabled={processingJob}
                    >
                      {processingJob ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                      Accept Job ($5 fee)
                    </Button>
                  )}
                  
                  {/* Active job status progression */}
                  {selectedJob.status === 'accepted' && (
                    <Button
                      className="w-full bg-purple-600 hover:bg-purple-500"
                      onClick={() => handleUpdateJobStatus(selectedJob.id, 'en_route')}
                      disabled={processingJob}
                    >
                      <Navigation className="h-4 w-4 mr-2" />
                      Start Driving (En Route)
                    </Button>
                  )}
                  
                  {selectedJob.status === 'en_route' && (
                    <Button
                      className="w-full bg-cyan-600 hover:bg-cyan-500"
                      onClick={() => handleUpdateJobStatus(selectedJob.id, 'arrived')}
                      disabled={processingJob}
                    >
                      <LocateFixed className="h-4 w-4 mr-2" />
                      I've Arrived
                    </Button>
                  )}
                  
                  {selectedJob.status === 'arrived' && (
                    <Button
                      className="w-full bg-orange-600 hover:bg-orange-500"
                      onClick={() => handleUpdateJobStatus(selectedJob.id, 'in_progress')}
                      disabled={processingJob}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Service
                    </Button>
                  )}
                  
                  {selectedJob.status === 'in_progress' && (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-500"
                      onClick={() => handleUpdateJobStatus(selectedJob.id, 'completed')}
                      disabled={processingJob}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Mark Complete
                    </Button>
                  )}
                  
                  {/* Chat button for active jobs */}
                  {['accepted', 'en_route', 'arrived', 'in_progress'].includes(selectedJob.status) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowJobDialog(false);
                        navigate(`/roadside/tracking/${selectedJob.id}`);
                      }}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      View Tracking & Chat
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Premium Subscription Dialog */}
        <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-400" />
                Upgrade to Premium
              </DialogTitle>
              <DialogDescription>
                Get more jobs and earn more with Premium membership
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Price */}
              <div className="text-center py-4 bg-slate-800/50 rounded-lg">
                <p className="text-4xl font-bold text-amber-400">${premiumStatus?.monthly_fee || 49.99}</p>
                <p className="text-slate-400">per month</p>
              </div>
              
              {/* Benefits */}
              <div className="space-y-3">
                <h4 className="font-medium">Premium Benefits:</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-green-400" />
                    </div>
                    <span><strong className="text-green-400">50% reduced platform fee</strong> - Pay only $2.50 per job</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-blue-400" />
                    </div>
                    <span><strong className="text-blue-400">Priority listing</strong> - Appear first in customer searches</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-amber-400" />
                    </div>
                    <span><strong className="text-amber-400">Featured badge</strong> - Stand out with premium badge</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-purple-400" />
                    </div>
                    <span><strong className="text-purple-400">Analytics access</strong> - Track your performance</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-cyan-400" />
                    </div>
                    <span><strong className="text-cyan-400">Priority support</strong> - Get help faster</span>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setShowPremiumDialog(false)} className="sm:flex-1">
                Maybe Later
              </Button>
              <Button 
                onClick={handleSubscribePremium} 
                disabled={processingPremium}
                className="bg-amber-500 hover:bg-amber-400 sm:flex-1"
              >
                {processingPremium ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Crown className="h-4 w-4 mr-2" />
                )}
                Subscribe Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Subscription Management Dialog */}
        <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
          <DialogContent className="max-w-md" data-testid="subscription-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-400" />
                Manage Subscription
              </DialogTitle>
              <DialogDescription>
                View and manage your Premium subscription
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Subscription Status */}
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <Crown className="h-6 w-6 text-amber-400" />
                  <div>
                    <p className="font-semibold text-amber-400">Premium Active</p>
                    <p className="text-xs text-slate-400">All benefits active</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-400">Monthly Fee</p>
                    <p className="font-medium">${premiumStatus?.monthly_fee || 49.99}/mo</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Next Billing</p>
                    <p className="font-medium">
                      {premiumStatus?.next_billing_date 
                        ? new Date(premiumStatus.next_billing_date).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Savings Summary */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-400 mb-2">Your Premium Savings</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Per job fee</p>
                    <p className="font-medium">$2.50 <span className="text-green-400 text-xs">(was $5.00)</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Estimated monthly savings</p>
                    <p className="font-medium text-green-400">
                      ${((earnings?.jobs_this_month || 0) * 2.5).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Member Since */}
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-slate-400">
                  Member since {premiumStatus?.subscribed_at 
                    ? new Date(premiumStatus.subscribed_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
            </div>
            
            <DialogFooter className="flex-col gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowSubscriptionDialog(false)}
                className="w-full"
              >
                Close
              </Button>
              <Button 
                variant="ghost"
                onClick={() => {
                  setShowSubscriptionDialog(false);
                  handleCancelPremium();
                }}
                disabled={processingPremium}
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="cancel-subscription-btn"
              >
                {processingPremium ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <X className="h-4 w-4 mr-2" />
                )}
                Cancel Subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
